import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { NavigationControl } from '@components/NavigationControl'
import { ScrollBar } from '@components/ScrollBar'
import { Dimensions } from 'react-native'
import { Dialog, Text, useMedia, XStack, YStack } from 'tamagui'

interface IExtraContentModalProps {
  open: boolean
  onClose: () => void
  onChangeContent: (i: number) => void
  children: React.ReactNode
  title: string
  mediaType: string
  disabledBtns?: {
    isFirstContent: boolean
    isLastContent: boolean
  }
  snapPoints?: number[]
  snapPointsMode?: 'percent' | 'fit' | 'constant' | 'mixed'
  zIndex?: number
  shouldAdapt?: boolean
}

export function ExtraContentModal({
  open,
  children,
  onClose,
  title,
  onChangeContent,
  disabledBtns,
  mediaType,
  snapPoints,
  snapPointsMode = 'percent',
  zIndex = 9999999,
  shouldAdapt = true,
}: IExtraContentModalProps) {
  const media = useMedia()
  const windowHeight = Dimensions.get('window').height
  const needScrollbar = mediaType === 'Text'
  const calculateMaxHeight = () => {
    if (media.gtLg) return windowHeight * 0.7
    if (media.gtMd) return 410
    if (media.gtSm) return windowHeight * 0.8
    return windowHeight
  }

  const modalMaxWidth = () => {
    if (media.gtMd) return 1064
    if (media.gtSm) return 672
    return 631
  }

  const calculateContainerPadding = () => {
    if (media.gtLg) return '$size.spacing-lg'
    if (media.gtSm) return '$size.spacing-md'
    return '$size.spacing-xxs'
  }

  const titleMarginBottom = media.gtSm ? 24 : 100
  const calculatePaddingBottom = media.gtSm ? 0 : 100

  return (
    <Modal
      onClose={onClose}
      open={open}
      maxWidth={modalMaxWidth()}
      zIndex={zIndex}
      shouldAdapt={shouldAdapt}
      snapPoints={snapPoints}
      snapPointsMode={snapPointsMode}
      disableDrag
    >
      <YStack flex={1} justifyContent="flex-start">
        {media.sm && (
          <XStack
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            marginVertical={'$spacing-inset-xxs'}
          >
            <Button
              icon
              position="absolute"
              top={5}
              right={5}
              onPress={onClose}
              outlineWidth={0}
              pressStyle={{ borderWidth: 0 }}
              focusStyle={{ borderWidth: 0 }}
            >
              <HeroIcon
                icon={['fal', 'xmark']}
                color="$icon-primary"
                size={20}
              />
            </Button>
          </XStack>
        )}
        <YStack
          marginTop="$size.spacing-md"
          flex={1}
          justifyContent="flex-start"
          padding={calculateContainerPadding()}
        >
          <YStack flex={1}>
            <XStack
              flexDirection="row"
              justifyContent="space-between"
              mb={20}
              alignItems="center"
              h="$size.spacing-lg"
            >
              <Dialog.Title marginBottom={titleMarginBottom} mb={0} flex={1}>
                <Text
                  ff="$mulishSemiBold"
                  color="$text-primary"
                  fontSize="$font-size-xs"
                  flexWrap="wrap"
                  flexShrink={1}
                  numberOfLines={2}
                  maxWidth={modalMaxWidth() - 100}
                  width="100%"
                >
                  {title}
                </Text>
              </Dialog.Title>
              <XStack flexDirection="row">
                <NavigationControl
                  action={() => onChangeContent(-1)}
                  chevronDirection="left"
                  disabled={disabledBtns?.isFirstContent}
                  size={24}
                />
                <NavigationControl
                  action={() => onChangeContent(1)}
                  chevronDirection="right"
                  disabled={disabledBtns?.isLastContent}
                  size={24}
                />
              </XStack>
            </XStack>
            <YStack flex={1}>
              {needScrollbar ? (
                <ScrollBar
                  maxHeight={calculateMaxHeight()}
                  scrollBarId="extraContentModal"
                  displayNativeIndicator={false}
                >
                  <YStack
                    paddingBottom={calculatePaddingBottom}
                    h="100%"
                    flex={1}
                  >
                    {children}
                  </YStack>
                </ScrollBar>
              ) : (
                <YStack paddingBottom={calculatePaddingBottom}>
                  {children}
                </YStack>
              )}
            </YStack>
          </YStack>
        </YStack>
      </YStack>
    </Modal>
  )
}
